/* eslint-disable no-nested-ternary */
/**
 * Signature for function that compares two values of type T
 *
 * @template T
 * @typedef {{
 *  (a: T, b: T): number
 *  reverse(a: T, b: T): number
 * }} CompareFn
 */

/**
 * Creates a reversed version of any compareFn
 *
 * @type {<T>(comapreFn: CompareFn<T>) => CompareFn<T>} */
export const reverse = compareFn => {
	const reversed = (a, b) => {
		const res = compareFn(a, b);
		return res && -res;
	};

	reversed.reverse = compareFn;

	return reversed;
};

const withReverse = fn => Object.defineProperty(fn, "reverse", { value: reverse(fn) });

/**
 * Compares two values of type T
 *
 * @template T
 * @type {CompareFn<T>}
 */
export const compare = (a, b) => (a > b ? 1 : a < b ? -1 : 0);
compare.reverse = reverse(compare);

/**
 * Creates a function that compare two unicode strings considering specified
 * locales and Intl.CollatorOptions
 *
 * @type {(locales?: string | string[], options?: Intl.CollatorOptions) => CompareFn<string>} */
export const localeCompare = (locales, options) =>
	withReverse((a, b) => a.localeCompare(b, locales, options));

/**
 * Creates a function that maps items to some other type and then compares received values
 *
 * @type {<A, B>(caster: (item: A) => B, compareFn?: CompareFn<B>) => CompareFn<A>} */
export const compareBy = (caster, compareFn = compare) =>
	withReverse((a, b) => compareFn(caster(a), caster(b)));

/**
 * Creates a function that moves items those meet a criteria specified by predicate to the head of the list
 *
 * @type {<T>(predicate: (item: T) => boolean) => CompareFn<T>} */
export const select = predicate =>
	withReverse((a, b) => {
		const shouldSelectA = Boolean(predicate(a));
		const shouldSelectB = Boolean(predicate(b));

		return shouldSelectA === shouldSelectB ? 0 : shouldSelectA ? -1 : 1;
	});
